import { useEffect, useState } from "react";
import { Loader2, Sparkles, Trash2, Wand2 } from "lucide-react";
import type { FilterColor, FilterDto, TestFilterResponse } from "../../../shared/types";
import { useQueryClient } from "@tanstack/react-query";
import {
  useClassifyBackfill,
  useClassifyStatus,
  useDeleteFilter,
  useGeneratePrompt,
  useSaveFilter,
  useTestFilter,
} from "../../hooks/useApi";
import { FILTER_COLORS, SWATCH_CLASSES } from "../filterColors";

const inputClass =
  "w-full rounded border border-stroke px-2.5 py-1.5 text-[13px] outline-none focus:border-accent";
const labelClass = "block pb-1 pt-2.5 text-[12px] font-medium text-ink-muted";

/**
 * AI Group studio: name + order, describe what to group, let the AI write the
 * classification prompt, edit it, dry-run it against recent mail, then save.
 * Used for both creating (initial undefined) and editing an existing group.
 */
export function AiGroupDialog({
  initial,
  defaultOrder,
  onClose,
}: {
  initial?: FilterDto;
  defaultOrder: number;
  onClose: () => void;
}) {
  const isNew = !initial;
  const [name, setName] = useState(initial?.name ?? "");
  const [order, setOrder] = useState<number>(initial?.order ?? defaultOrder);
  const [color, setColor] = useState<FilterColor>(initial?.color ?? "blue");
  const [enabled, setEnabled] = useState(initial?.enabled ?? true);
  const [email, setEmail] = useState(initial?.email ?? "");
  const [description, setDescription] = useState("");
  const [prompt, setPrompt] = useState(initial?.prompt ?? "");
  const [test, setTest] = useState<TestFilterResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Re-evaluate emails that already carry a verdict for this group.
  const [override, setOverride] = useState(false);
  // "edit" = the form; "running" = live classification progress after save.
  const [phase, setPhase] = useState<"edit" | "running">("edit");

  const generate = useGeneratePrompt();
  const runTest = useTestFilter();
  const saveFilter = useSaveFilter();
  const deleteFilter = useDeleteFilter();
  const classifyBackfill = useClassifyBackfill();
  const { data: classify } = useClassifyStatus();
  const queryClient = useQueryClient();

  // While classification runs, refresh labels/counts on every progress tick so
  // the email list shows dots appearing live.
  useEffect(() => {
    if (phase !== "running") return;
    queryClient.invalidateQueries({ queryKey: ["labels"] });
    queryClient.invalidateQueries({ queryKey: ["filter-counts"] });
  }, [phase, classify?.processed, queryClient]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const doGenerate = () => {
    setError(null);
    generate.mutate(
      { name, description },
      {
        onSuccess: (res) => {
          setPrompt(res.prompt);
          setTest(null); // stale test results don't apply to the new prompt
        },
        onError: (e) => setError(e.message),
      },
    );
  };

  const doTest = () => {
    setError(null);
    setTest(null);
    runTest.mutate(
      { name, prompt },
      {
        onSuccess: setTest,
        onError: (e) => setError(e.message),
      },
    );
  };

  const doSave = (run: boolean) => {
    setError(null);
    saveFilter.mutate(
      { id: initial?.id, name, prompt, color, enabled, order, email: email.trim() || null },
      {
        onSuccess: (saved) => {
          if (!run) {
            onClose(); // plain save — no classification pass
            return;
          }
          // Classify the whole stored history for this group (already-evaluated
          // emails are skipped) and flip the dialog into live progress. Seed the
          // status cache with the 202 snapshot so 2s polling starts instantly.
          classifyBackfill.mutate(
            { filterId: saved.id, override },
            { onSuccess: (snap) => queryClient.setQueryData(["classify-status"], snap) },
          );
          setPhase("running");
        },
        onError: (e) => setError(e.message),
      },
    );
  };

  const doDelete = () => {
    if (!initial) return;
    if (window.confirm(`Delete the "${initial.name}" AI group?`)) {
      deleteFilter.mutate(initial.id, { onSuccess: onClose });
    }
  };

  const promptChanged = !isNew && prompt.trim() !== initial!.prompt;

  return (
    <div
      className="fixed inset-0 z-[80] grid place-items-center bg-black/30"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="flex max-h-[88vh] w-[520px] max-w-[94vw] flex-col overflow-y-auto rounded-lg border border-stroke bg-white p-5 shadow-xl"
        role="dialog"
        aria-label={isNew ? "New AI group" : `Edit AI group ${initial!.name}`}
      >
        {phase === "running" ? (
          <ClassifyProgress
            name={name}
            running={!!classify?.running}
            processed={classify?.processed ?? 0}
            total={classify?.total ?? 0}
            matched={classify?.matched ?? 0}
            alreadyCategorized={classify?.alreadyCategorized ?? 0}
            costUsd={classify?.costUsd ?? 0}
            error={classify?.error}
            onClose={onClose}
          />
        ) : (
          <>
        <h2 className="text-[15px] font-semibold">{isNew ? "New AI group" : "Edit AI group"}</h2>

        <div className="flex items-end gap-3">
          <div className="flex-1">
            <label className={labelClass}>Name</label>
            <input
              className={inputClass}
              autoFocus={isNew}
              placeholder="Newsletters"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="w-20">
            <label className={labelClass}>Order</label>
            <input
              type="number"
              min={1}
              className={inputClass}
              value={order}
              onChange={(e) => setOrder(Number(e.target.value) || defaultOrder)}
            />
          </div>
        </div>

        <div className="flex items-center justify-between pt-2.5">
          <div className="flex items-center gap-1">
            {FILTER_COLORS.map((c) => (
              <button
                key={c}
                onClick={() => setColor(c)}
                title={c}
                aria-label={`Color ${c}`}
                className={`size-4 rounded-full ${SWATCH_CLASSES[c]} ${
                  color === c ? "ring-2 ring-accent ring-offset-1" : ""
                }`}
              />
            ))}
          </div>
          <label className="flex items-center gap-1.5 text-[12px] text-ink-muted">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
            />
            Enabled
          </label>
        </div>

        <label className={labelClass}>What do you want to group together?</label>
        <textarea
          className={`${inputClass} h-16 resize-y`}
          placeholder="e.g. newsletters and weekly digests I'm subscribed to, but not order confirmations"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
        <button
          onClick={doGenerate}
          disabled={!description.trim() || generate.isPending}
          className="mt-2 flex items-center gap-1.5 self-start rounded bg-accent px-3 py-1.5 text-[12px] font-medium text-white hover:bg-accent-hover disabled:opacity-40"
        >
          {generate.isPending ? (
            <Loader2 size={13} className="animate-spin" />
          ) : (
            <Wand2 size={13} />
          )}
          {generate.isPending ? "Writing prompt…" : "Create AI Prompt"}
        </button>

        <label className={labelClass}>AI Prompt (edit as needed)</label>
        <textarea
          className={`${inputClass} h-56 resize-y`}
          placeholder="Generate a prompt above, or write your own…"
          value={prompt}
          onChange={(e) => {
            setPrompt(e.target.value);
            setTest(null);
          }}
        />

        <button
          onClick={doTest}
          disabled={!prompt.trim() || runTest.isPending}
          className="mt-2 flex items-center gap-1.5 self-start rounded border border-accent px-3 py-1.5 text-[12px] font-medium text-accent hover:bg-accent-subtle disabled:opacity-40"
        >
          {runTest.isPending ? (
            <Loader2 size={13} className="animate-spin" />
          ) : (
            <Sparkles size={13} />
          )}
          {runTest.isPending ? "Testing against your recent emails…" : "Test AI Group"}
        </button>

        <label className={labelClass}>Email</label>
        <input
          type="email"
          className={inputClass}
          placeholder="address for future actions on this group (optional)"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />

        {test && (
          <div className="mt-2 rounded border border-stroke bg-surface p-2">
            <p className="pb-1 text-[12px] font-semibold">
              {test.matches.length} of {test.total} recent emails match
            </p>
            <div className="max-h-40 overflow-y-auto">
              {test.matches.length === 0 && (
                <p className="text-[12px] text-ink-muted">
                  No matches — loosen the prompt or test again after new mail arrives.
                </p>
              )}
              {test.matches.map((m) => (
                <div key={m.id} className="flex items-baseline gap-2 py-0.5 text-[12px]">
                  <span className="w-32 shrink-0 truncate text-ink-muted">
                    {m.from?.name ?? m.from?.address ?? "unknown"}
                  </span>
                  <span className="min-w-0 flex-1 truncate">{m.subject}</span>
                  <span className="shrink-0 text-[11px] text-ink-muted">
                    {new Date(m.receivedDateTime).toLocaleDateString()}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {promptChanged && (
          <p className="pt-2 text-[12px] text-amber-700">
            The prompt changed — saving clears this group's cached results and re-runs it over
            your stored mail.
          </p>
        )}
        {error && <p className="pt-2 text-[12px] text-red-700">{error}</p>}

        <div className="flex items-center justify-between pt-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => doSave(true)}
              disabled={!name.trim() || !prompt.trim() || saveFilter.isPending}
              className="rounded bg-accent px-4 py-1.5 text-[13px] font-medium text-white hover:bg-accent-hover disabled:opacity-40"
            >
              {saveFilter.isPending ? "Saving…" : "Save and run"}
            </button>
            <button
              onClick={() => doSave(false)}
              disabled={!name.trim() || !prompt.trim() || saveFilter.isPending}
              className="rounded border border-accent px-4 py-1.5 text-[13px] font-medium text-accent hover:bg-accent-subtle disabled:opacity-40"
              title="Save changes without classifying stored emails"
            >
              Save
            </button>
            <label
              className="flex items-center gap-1.5 text-[12px] text-ink-muted"
              title="Re-evaluate emails that already have a verdict for this group"
            >
              <input
                type="checkbox"
                checked={override}
                onChange={(e) => setOverride(e.target.checked)}
              />
              Override current category
            </label>
          </div>
          <div className="flex items-center gap-2">
            {!isNew && (
              <button
                onClick={doDelete}
                title="Delete group"
                aria-label="Delete group"
                className="grid size-7 place-items-center rounded text-ink-muted hover:bg-red-50 hover:text-red-700"
              >
                <Trash2 size={14} />
              </button>
            )}
            <button
              onClick={onClose}
              className="rounded border border-stroke px-4 py-1.5 text-[13px] hover:bg-black/5"
            >
              Cancel
            </button>
          </div>
        </div>
          </>
        )}
      </div>
    </div>
  );
}

/** Live progress view shown in the dialog right after "Save and run". */
function ClassifyProgress({
  name,
  running,
  processed,
  total,
  matched,
  alreadyCategorized,
  costUsd,
  error,
  onClose,
}: {
  name: string;
  running: boolean;
  processed: number;
  total: number;
  matched: number;
  alreadyCategorized: number;
  costUsd: number;
  error?: string;
  onClose: () => void;
}) {
  const pct = total > 0 ? Math.min(100, Math.round((processed / total) * 100)) : 0;
  const done = !running && processed > 0;
  return (
    <div>
      <h2 className="flex items-center gap-2 text-[15px] font-semibold">
        {running ? <Loader2 size={15} className="animate-spin text-accent" /> : <Sparkles size={15} className="text-green-700" />}
        {running ? `Classifying "${name}"…` : done ? `"${name}" is ready` : `Starting "${name}"…`}
      </h2>

      <div className="mt-4 h-2 w-full overflow-hidden rounded-full bg-black/10">
        <div
          className={`h-full rounded-full transition-all duration-500 ${running ? "bg-accent" : "bg-green-600"}`}
          style={{ width: `${done ? 100 : pct}%` }}
        />
      </div>
      <p className="pt-2 text-[13px] text-ink">
        {processed.toLocaleString()} of {total.toLocaleString()} emails evaluated ·{" "}
        <span className="font-semibold">{matched.toLocaleString()} match</span>
      </p>
      <p className="pt-0.5 text-[12px] text-ink-muted">
        Already categorized: {alreadyCategorized.toLocaleString()} · OpenRouter cost:{" "}
        {costUsd < 0.01 ? `$${costUsd.toFixed(4)}` : `$${costUsd.toFixed(2)}`}
      </p>
      <p className="pt-1 text-[12px] text-ink-muted">
        The email list updates live — colored dots appear as emails are categorized. You can close
        this dialog; classification continues in the background (progress stays visible in the
        bottom-right panel).
      </p>
      {error && <p className="pt-2 text-[12px] text-red-700">{error}</p>}

      <div className="flex justify-end pt-4">
        <button
          onClick={onClose}
          className="rounded bg-accent px-4 py-1.5 text-[13px] font-medium text-white hover:bg-accent-hover"
        >
          {running ? "Close (keeps running)" : "Done"}
        </button>
      </div>
    </div>
  );
}
